/**
 * Impersonation session helpers (platform-owner "enter tenant" / "view as").
 *
 * Entering a tenant swaps the operator's tokens for a token minted as the tenant's owner. We
 * stash the operator's own tokens under backup keys so the banner can restore them on exit —
 * the operator never has to log back in. State lives in localStorage; enter/exit do a full
 * page reload so every query cache re-fetches under the new identity (no cross-tenant bleed).
 */
import { impersonateTenant } from '@/lib/api/platform';

const ACCESS = 'access_token';
const REFRESH = 'refresh_token';
const BK_ACCESS = 'op_backup_access_token';
const BK_REFRESH = 'op_backup_refresh_token';
const TENANT_NAME = 'impersonating_tenant';

export function isImpersonating(): boolean {
  if (typeof window === 'undefined') return false;
  return !!localStorage.getItem(TENANT_NAME);
}

export function impersonatingTenantName(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TENANT_NAME);
}

/** Enter a tenant: back up operator tokens, swap in the impersonation token, reload. */
export async function enterTenant(tenantId: string): Promise<void> {
  const res = await impersonateTenant(tenantId);

  // Don't clobber an existing backup if (somehow) already impersonating.
  if (!localStorage.getItem(BK_ACCESS)) {
    const curAccess = localStorage.getItem(ACCESS);
    const curRefresh = localStorage.getItem(REFRESH);
    if (curAccess) localStorage.setItem(BK_ACCESS, curAccess);
    if (curRefresh) localStorage.setItem(BK_REFRESH, curRefresh);
  }

  localStorage.setItem(ACCESS, res.access_token);
  if (res.refresh_token) localStorage.setItem(REFRESH, res.refresh_token);
  localStorage.setItem(TENANT_NAME, res.tenant_name || 'tenant');

  window.location.assign('/dashboard');
}

/** Exit impersonation: restore the operator's tokens and return to the admin screen. */
export function exitImpersonation(): void {
  const bkAccess = localStorage.getItem(BK_ACCESS);
  const bkRefresh = localStorage.getItem(BK_REFRESH);
  if (bkAccess) localStorage.setItem(ACCESS, bkAccess);
  else localStorage.removeItem(ACCESS);
  if (bkRefresh) localStorage.setItem(REFRESH, bkRefresh);
  else localStorage.removeItem(REFRESH);

  localStorage.removeItem(BK_ACCESS);
  localStorage.removeItem(BK_REFRESH);
  localStorage.removeItem(TENANT_NAME);

  window.location.assign('/admin/tenants');
}
